import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProjectLabels, useClients } from "@/hooks/use-app-queries";
import { createClient, createProject, type ClientItem, type ProjectLabel } from "@/lib/projects-clients";
import { invalidateClientData, invalidateProjectData } from "@/lib/query-invalidation";
import { formatUsPhoneDisplay, normalizeUsPhoneDigits } from "@/lib/us-phone";
import { useToast } from "@/hooks/use-toast";

type ClientForm = {
  name: string;
  companyName: string;
  type: string;
  email: string;
  phone: string;
  address: string;
};

type ProjectFlowForm = {
  name: string;
  code: string;
  clientId: string;
  jobSiteAddress: string;
  labelIds: string[];
};

const emptyForm: ClientForm = {
  name: "",
  companyName: "",
  type: "General Contractor",
  email: "",
  phone: "",
  address: "",
};

const emptyProjectForm: ProjectFlowForm = {
  name: "",
  code: "",
  clientId: "",
  jobSiteAddress: "",
  labelIds: [],
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ClientFlowDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const projectLabelsQuery = useProjectLabels();
  const clientsQuery = useClients();
  const projectLabels = useMemo(() => projectLabelsQuery.data ?? [], [projectLabelsQuery.data]);
  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);

  const [form, setForm] = useState<ClientForm>(emptyForm);
  const [projectForm, setProjectForm] = useState<ProjectFlowForm>(emptyProjectForm);
  const [projectLabelSearch, setProjectLabelSearch] = useState("");
  const [clientModalStep, setClientModalStep] = useState<"client" | "project">("client");
  const [flowClient, setFlowClient] = useState<ClientItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingAndContinuing, setSavingAndContinuing] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [savingProjectAndContinuing, setSavingProjectAndContinuing] = useState(false);

  const filteredProjectLabels = projectLabels.filter((label) =>
    label.name.toLowerCase().includes(projectLabelSearch.trim().toLowerCase()),
  );

  const duplicateNameMatches = useMemo(() => {
    const normalized = form.name.trim().toLowerCase();
    if (normalized.length < 3) return [];
    return clients.filter((client) => {
      const candidate = client.name.trim().toLowerCase();
      return candidate.includes(normalized) || normalized.includes(candidate);
    });
  }, [clients, form.name]);

  const resetState = () => {
    setForm(emptyForm);
    setProjectForm(emptyProjectForm);
    setProjectLabelSearch("");
    setFlowClient(null);
    setClientModalStep("client");
    setSaving(false);
    setSavingAndContinuing(false);
    setSavingProject(false);
    setSavingProjectAndContinuing(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetState();
    }
    onOpenChange(nextOpen);
  };

  const submitForm = async (mode: "save" | "continue_to_project" = "save") => {
    if (!form.name.trim()) {
      toast({
        title: t("clients.nameRequired"),
        description: t("clients.enterClientName"),
        variant: "destructive",
      });
      return;
    }

    if (mode === "continue_to_project") {
      setSavingAndContinuing(true);
    } else {
      setSaving(true);
    }

    try {
      const created = await createClient({
        name: form.name.trim(),
        companyName: form.companyName.trim() || undefined,
        type: form.type || undefined,
        email: form.email || undefined,
        phone: normalizeUsPhoneDigits(form.phone) || undefined,
        address: form.address || undefined,
      });

      toast({
        title:
          mode === "continue_to_project"
            ? t("clients.clientCreatedContinueProject")
            : t("clients.clientCreated"),
      });

      await invalidateClientData(queryClient);

      if (mode === "continue_to_project") {
        setFlowClient(created);
        setProjectForm({
          ...emptyProjectForm,
          clientId: created.id,
          jobSiteAddress: created.address?.trim() || "",
        });
        setProjectLabelSearch("");
        setClientModalStep("project");
        return;
      }

      handleOpenChange(false);
    } catch (e) {
      toast({
        title: t("common.createFailed"),
        description: e instanceof Error ? e.message : t("common.requestFailed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setSavingAndContinuing(false);
    }
  };

  const submitProjectStep = async (mode: "save" | "continue_to_ticket" = "save") => {
    if (!flowClient?.id) return;
    if (!projectForm.name.trim()) {
      toast({
        title: t("projects.projectNameRequired"),
        description: t("projects.enterProjectName"),
        variant: "destructive",
      });
      return;
    }

    if (mode === "continue_to_ticket") {
      setSavingProjectAndContinuing(true);
    } else {
      setSavingProject(true);
    }

    try {
      const created = await createProject({
        name: projectForm.name.trim(),
        code: projectForm.code.trim() || undefined,
        clientId: flowClient.id,
        jobSiteAddress: projectForm.jobSiteAddress.trim() || undefined,
        status: "ACTIVE",
        labelIds: projectForm.labelIds,
      });

      toast({
        title:
          mode === "continue_to_ticket"
            ? t("projects.projectCreatedContinueTicket")
            : t("projects.projectCreated"),
      });

      await invalidateProjectData(queryClient);
      handleOpenChange(false);

      if (mode === "continue_to_ticket") {
        navigate(`/tickets/new?projectId=${encodeURIComponent(created.id)}&type=estimate`);
      }
    } catch (e) {
      toast({
        title: t("common.createFailed"),
        description: e instanceof Error ? e.message : t("projects.unableToSaveProject"),
        variant: "destructive",
      });
    } finally {
      setSavingProject(false);
      setSavingProjectAndContinuing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-[#0C1F4F]">
            {clientModalStep === "project" ? t("projects.addProject") : t("clients.addClient")}
          </DialogTitle>
          <DialogDescription className="text-[#0C1F4F]/80">
            {clientModalStep === "project"
              ? t("clients.continueSetupForClient", { client: flowClient?.name ?? t("clients.thisClient") })
              : t("clients.enterDetailsDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-hidden">
          <div
            className={`flex w-[200%] transition-transform duration-300 ease-out ${clientModalStep === "project" ? "-translate-x-1/2" : "translate-x-0"}`}
          >
            <div className="w-1/2 shrink-0 pr-2">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-[#0C1F4F]">
                    {t("clients.clientName")} <span className="text-accent">*</span>
                  </Label>
                  <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder={t("clients.clientNamePlaceholder")} />
                  {duplicateNameMatches.length > 0 ? (
                    <p className="text-xs text-amber-600">
                      {t("clients.similarClientExists", { names: duplicateNameMatches.slice(0, 2).map((c) => c.name).join(", ") })}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#0C1F4F]">{t("clients.companyName")}</Label>
                  <Input
                    value={form.companyName}
                    onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))}
                    placeholder={t("clients.companyNamePlaceholder")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#0C1F4F]">{t("clients.type")}</Label>
                  <Select value={form.type} onValueChange={(value) => setForm((p) => ({ ...p, type: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("clients.selectType")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General Contractor">{t("clients.types.generalContractor")}</SelectItem>
                      <SelectItem value="Owner">{t("clients.types.owner")}</SelectItem>
                      <SelectItem value="Property Manager">{t("clients.types.propertyManager")}</SelectItem>
                      <SelectItem value="Developer">{t("clients.types.developer")}</SelectItem>
                      <SelectItem value="Other">{t("clients.types.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">{t("clients.primaryApproverEmail")}</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder={t("clients.primaryApproverEmailPlaceholder")} />
                    <p className="text-xs text-muted-foreground">
                      {t("clients.primaryApproverEmailHelp")}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">{t("createAccount.phone")}</Label>
                    <Input
                      value={form.phone}
                      inputMode="numeric"
                      maxLength={14}
                      onChange={(e) => setForm((p) => ({ ...p, phone: formatUsPhoneDisplay(e.target.value) }))}
                      placeholder={t("clients.phonePlaceholder")}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#0C1F4F]">{t("clients.address")}</Label>
                  <Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} placeholder={t("clients.addressPlaceholder")} />
                </div>
              </div>

              <DialogFooter className="mt-5 gap-2.5">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>{t("login.cancel")}</Button>
                <Button
                  variant="outline"
                  onClick={() => void submitForm("save")}
                  disabled={saving || savingAndContinuing}
                >
                  {saving ? t("common.saving") : t("clients.addClient")}
                </Button>
                <Button
                  onClick={() => void submitForm("continue_to_project")}
                  disabled={saving || savingAndContinuing}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  {savingAndContinuing ? t("common.saving") : t("clients.continueToProject")}
                </Button>
              </DialogFooter>
            </div>

            <div className="w-1/2 shrink-0 pl-2">
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-[#0C1F4F]">
                    {t("projects.projectName")} <span className="text-accent">*</span>
                  </Label>
                  <Input
                    value={projectForm.name}
                    onChange={(e) => setProjectForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder={t("projects.projectNamePlaceholder")}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#0C1F4F]">{t("projects.projectCode")}</Label>
                    <Input
                      value={projectForm.code}
                      onChange={(e) => setProjectForm((p) => ({ ...p, code: e.target.value }))}
                      placeholder={t("projects.projectCodePlaceholder")}
                    />
                  </div>
                  <div className="space-y-1.5 sm:pt-0.5">
                    <Label className="text-[#0C1F4F]">
                      {t("nav.clients")} <span className="text-accent">*</span>
                    </Label>
                    <div className="flex h-9 items-center rounded-md border bg-muted/20 px-3 text-sm text-foreground">
                      {flowClient?.name ?? t("clients.selectedClient")}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[#0C1F4F]">{t("projects.jobSiteAddress")}</Label>
                  <Input
                    value={projectForm.jobSiteAddress}
                    onChange={(e) => setProjectForm((p) => ({ ...p, jobSiteAddress: e.target.value }))}
                    placeholder={t("clients.addressPlaceholder")}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#0C1F4F]">{t("projects.labels")}</Label>
                  {projectForm.labelIds.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {projectForm.labelIds
                        .map((labelId) => projectLabels.find((label) => label.id === labelId))
                        .filter((label): label is ProjectLabel => Boolean(label))
                        .map((label) => (
                          <span
                            key={label.id}
                            className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
                            style={{
                              color: label.color,
                              borderColor: `${label.color}40`,
                              backgroundColor: `${label.color}14`,
                            }}
                          >
                            <span
                              className="mr-1 h-1.5 w-1.5 rounded-full"
                              style={{ backgroundColor: label.color }}
                            />
                            {label.name}
                          </span>
                        ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No labels selected.</p>
                  )}

                  {projectLabels.length ? (
                    <>
                      <Input
                        value={projectLabelSearch}
                        onChange={(e) => setProjectLabelSearch(e.target.value)}
                        placeholder="Search labels..."
                      />
                      <div className="max-h-40 overflow-y-auto rounded-md border">
                        {filteredProjectLabels.length ? (
                          filteredProjectLabels.map((label) => {
                            const checked = projectForm.labelIds.includes(label.id);
                            return (
                              <label
                                key={label.id}
                                className="flex cursor-pointer items-center justify-between gap-3 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/30"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      setProjectForm((prev) => ({
                                        ...prev,
                                        labelIds: checked
                                          ? prev.labelIds.filter((id) => id !== label.id)
                                          : [...prev.labelIds, label.id],
                                      }))
                                    }
                                  />
                                  <span
                                    className="inline-flex h-2 w-2 rounded-full"
                                    style={{ backgroundColor: label.color }}
                                  />
                                  <span className="truncate">{label.name}</span>
                                </span>
                                <span
                                  className="inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium"
                                  style={{
                                    color: label.color,
                                    borderColor: `${label.color}40`,
                                    backgroundColor: `${label.color}14`,
                                  }}
                                >
                                  {label.name}
                                </span>
                              </label>
                            );
                          })
                        ) : (
                          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                            No labels match your search.
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Create project labels in Settings before assigning them here.
                    </p>
                  )}
                </div>
              </div>

              <DialogFooter className="mt-5 gap-2.5">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
                <Button
                  variant="outline"
                  onClick={() => void submitProjectStep("save")}
                  disabled={savingProject || savingProjectAndContinuing}
                >
                  {savingProject ? "Saving..." : "Add Project"}
                </Button>
                <Button
                  onClick={() => void submitProjectStep("continue_to_ticket")}
                  disabled={savingProject || savingProjectAndContinuing}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  {savingProjectAndContinuing ? "Saving..." : "Continue to Ticket"}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
